import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const vignetteFocusPullSkill: Skill = {
    name: SKILL_NAMES.vignetteFocusPull,
    kind: 'codeo',
    description:
        'Close a soft dark vignette in around one part of the frame to force attention there, ' +
        'then release it — for isolating a figure, a card, or a moment without moving anything.',
    instructions: `# Vignette Focus Pull

A soft dark falloff closes in from the frame edges toward one point, everything outside it drops away, and after a beat it opens back out. Nothing moves. It is the quietest way to say "only this matters right now", and it works over layouts that are already too busy to rearrange.

The vignette is one full-frame radial gradient whose stops and centre tween. Because it is a single element, it composites cheaply even at full frame.

## Markup

The vignette sits above the content and below anything that must never be dimmed.

\`\`\`html
<div id="focus-stage" style="position: absolute; inset: 0; overflow: hidden;">
  <div id="content" style="position: absolute; inset: 0;">
    <div style="position: absolute; left: 180px; top: 300px; font-family: var(--l5-font-0), sans-serif; font-size: 44px; font-weight: 500; color: var(--l5-color-1, #5b6472);">Twelve regions</div>
    <div id="focus-target" style="position: absolute; left: 760px; top: 440px; font-family: var(--l5-font-0), sans-serif; font-size: 132px; font-weight: 900; color: var(--l5-color-0, #101820);">One</div>
    <div style="position: absolute; left: 1420px; top: 700px; font-family: var(--l5-font-0), sans-serif; font-size: 44px; font-weight: 500; color: var(--l5-color-1, #5b6472);">One rulebook</div>
  </div>
  <div id="vignette" style="position: absolute; inset: 0; background: radial-gradient(circle at 46% 52%, rgba(0,0,0,0) 100%, rgba(0,0,0,0.82) 160%);"></div>
</div>
\`\`\`

## Timeline code

Size the vignette in **pixels, measured off the element you are isolating**. A percentage stop in a radial gradient resolves against the gradient's own size rather than the frame, so a number like \`16%\` tells you nothing about how much of the frame stays clear — and it is how you end up dimming the very thing the shot is about.

**The vignette is two events in a scene that needs more than two.** The content still has to arrive before the falloff closes on it, and the hold — which is the longest single stretch of the technique — needs something moving in it or the frame is simply a still image for as long as the vignette is shut.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const vignetteEl = document.querySelector('#vignette');
const targetEl = document.querySelector('#focus-target');
const CLOSE = 1.1;
const HOLD = 1.6;

// The clear radius has to contain the element, with room to breathe. Measured, not guessed.
const box = targetEl.getBoundingClientRect();
const CLEAR = Math.max(box.width, box.height) / 2 + 90;
const SOFT = 420;                       // width of the falloff itself
const cx = box.left + box.width / 2;
const cy = box.top + box.height / 2;
const falloff = { r: 1600 };            // open: bigger than the frame's diagonal

const paint = () => {
    vignetteEl.style.background =
        \`radial-gradient(circle at \${cx}px \${cy}px, rgba(0,0,0,0) \${falloff.r}px, rgba(0,0,0,0.82) \${falloff.r + SOFT}px)\`;
};
paint();

// The content arrives first — the vignette has to close on something.
gsap.set('#content > *', { y: 18, opacity: 0 });
tl.to('#content > *', { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out', stagger: 0.12 }, 0.15);

tl.to(falloff, { r: CLEAR, duration: 0.8, ease: 'power2.inOut', onUpdate: paint }, CLOSE);

// A slow push on the isolated element runs through the hold, so the closed
// vignette is a held moment rather than a frozen frame.
tl.to('#focus-target', { scale: 1.05, duration: HOLD + 0.8, ease: 'none' }, CLOSE);

tl.to(falloff, { r: 1600, duration: 0.7, ease: 'power2.inOut', onUpdate: paint }, CLOSE + HOLD);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Centre**: the centre comes from the element's own measured box, so it lands on the subject automatically. An off-centre vignette is far more interesting than a symmetric one and reads as deliberate — put the subject off-centre in the layout rather than nudging the gradient.
- **Depth**: 0.82 alpha nearly blacks out the surroundings. 0.45 dims them while keeping them readable, which suits "this one among these" rather than "only this".
- **Stay closed**: dropping the reopen leaves the frame isolated for the rest of the scene — the right choice when the graphic ends on that element.
- **Elliptical**: for a wide headline, \`ellipse \${box.width / 2 + 90}px \${box.height / 2 + 70}px at …\` hugs the shape better than a circle and stops the falloff eating the ends of the line. Keep both radii in px for the same reason the circle is.
- **Colour instead of black**: a dark palette colour reads as grading rather than as a mask, and stays on-brand.
- **With a blur**: pairing the close with a blur on the surrounding content compounds the isolation — see \`${SKILL_NAMES.softFocusResolve}\`.

## Pitfalls

- Rebuild the whole gradient string in \`onUpdate\`; GSAP cannot interpolate the stops of a gradient function on its own.
- **The clear radius must contain what you are isolating.** Measure the element and add padding; a closed radius smaller than the headline crops it mid-word, which reads as a rendering fault rather than as emphasis. This is the single most common way this technique fails.
- **Don't size the gradient's ellipse and its stops on the same value.** Shrinking both at once collapses the clear area far faster than either number suggests, and a 15% stop on an ellipse that is itself 8% of the frame clears barely a hundred pixels.
- Stops must ascend. \`rgba(…,0) 100%\` followed by \`rgba(…,.94) 75%\` is out of order, so the browser clamps the second up to 100% and the soft falloff you thought you had becomes a hard edge.
- Keep the gap between the two stops constant (\`SOFT\` here) or the falloff hardens as it closes and the vignette turns into a spotlight edge.
- The vignette element must be full-frame and above the content it dims — check the DOM order, not just the z-index.
- Don't dim text the viewer still needs. Anything at 0.82 under the falloff is effectively gone.
- Reopen it, unless the scene ends here. A permanently closed vignette makes the rest of the graphic feel broken.
- **Two tweens is not a scene.** A composition whose only motion is the vignette closing and reopening is a still image for the opening, a still image through the hold, and a still image after. The close and the open are punctuation; the content entering and something living inside the hold are what they punctuate.
- Don't stack a camera push *and* an ambient drift on top of the slow push through the hold. One carried motion is the budget.
- A large full-frame gradient repainted every frame is the expensive part; don't stack two of them.`,
};
